/**
 * Lead-capture link construction (CRM-130).
 *
 * The three shapes the public routes accept (see backend `routes/publicLeads.ts`):
 * a rep link carrying a preset event code, the cold website variant with a code,
 * and a bare rep link where the coach picks the event from the form's dropdown.
 * Codes are immutable and printed on collateral, so these strings outlive the UI.
 */
export type LeadLinkKind = 'REP_SOURCE' | 'SOURCE_ONLY' | 'REP_ONLY';

export const buildLeadLink = (
  origin: string,
  kind: LeadLinkKind,
  opts: { repUserId?: string | null; sourceCode?: string | null },
): string | null => {
  const base = origin.replace(/\/+$/, '');
  const repUserId = opts.repUserId?.trim() || '';
  const sourceCode = opts.sourceCode?.trim() || '';

  if (kind === 'REP_SOURCE') {
    if (!repUserId || !sourceCode) return null;
    return `${base}/lead/${repUserId}?source=${encodeURIComponent(sourceCode)}`;
  }
  if (kind === 'SOURCE_ONLY') {
    if (!sourceCode) return null;
    return `${base}/lead?source=${encodeURIComponent(sourceCode)}`;
  }
  if (!repUserId) return null;
  return `${base}/lead/${repUserId}`;
};

/** Filename for a downloaded QR — slug of the parts that identify the link. */
export const qrFileName = (parts: (string | null | undefined)[]): string => {
  const slug = parts
    .filter(Boolean)
    .join('-')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `lead-qr-${slug || 'link'}.png`;
};

/**
 * Copy to the clipboard, falling back to a hidden-textarea `execCommand` copy:
 * `navigator.clipboard` only exists in a secure context, and the CRM is reached
 * over plain http on some internal hosts.
 */
export const copyText = async (text: string): Promise<boolean> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to the legacy path */
    }
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};
